import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';

// Layer components
import { BackgroundLayer } from '../layers/BackgroundLayer';
import { AnchorLayer } from '../layers/AnchorLayer';
import { LogoLayer } from '../layers/LogoLayer';
import { TopBarLayer } from '../layers/TopBarLayer';
import { LowerThirdLayer } from '../layers/LowerThirdLayer';
import { TickerLayer } from '../layers/TickerLayer';
import { BreakingNewsBannerLayer } from '../layers/BreakingNewsBannerLayer';
import { WatermarkLayer } from '../layers/WatermarkLayer';
import { AudioLayer } from '../layers/AudioLayer';
import { ImageLayer, VideoLayer } from '../layers/ImageLayer';
import { HeadlineLayer, SubheadlineLayer } from '../layers/HeadlineLayer';

import type {
  CompositionProps,
  HeadlineSegment,
  TemplateLayer,
  BackgroundLayer as BackgroundLayerType,
  AnchorLayer as AnchorLayerType,
  LogoLayer as LogoLayerType,
  TopBarLayer as TopBarLayerType,
  LowerThirdLayer as LowerThirdLayerType,
  TickerLayer as TickerLayerType,
  BreakingNewsBannerLayer as BannerLayerType,
  WatermarkLayer as WatermarkLayerType,
  AudioLayer as AudioLayerType,
  ImageLayer as ImageLayerType,
  VideoLayer as VideoLayerType,
} from '../../types';

// ─── Segment helpers ──────────────────────────────────────────

function getActiveSegmentText(
  segments: HeadlineSegment[],
  frame: number,
  fps: number
): string {
  const sec = frame / fps;
  const active = segments.find(
    (s) => sec >= s.startSec && (s.endSec < 0 || sec < s.endSec)
  );
  return active?.text ?? '';
}

// ─── Composition ──────────────────────────────────────────────

export const NewsComposition: React.FC<CompositionProps> = (props) => {
  const {
    templateConfig,
    headline,
    subheadline,
    headlineSegments,
    subheadlineSegments,
    breakingNewsText,
    tickerText,
    reporterName,
    location,
    date,
    assets,
  } = props;
  const { layers, colorPalette } = templateConfig;

  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Resolve active headline / subheadline text.
  // Segments (multiple texts at different times) override the plain field.
  const activeHeadline =
    headlineSegments && headlineSegments.length > 0
      ? getActiveSegmentText(headlineSegments, frame, fps)
      : headline;

  const activeSubheadline =
    subheadlineSegments && subheadlineSegments.length > 0
      ? getActiveSegmentText(subheadlineSegments, frame, fps)
      : subheadline;

  // Sort by zIndex for correct render order
  const sortedLayers = [...layers].sort((a, b) => a.position.zIndex - b.position.zIndex);

  return (
    <AbsoluteFill style={{ backgroundColor: colorPalette.background }}>
      {sortedLayers.map((layer) =>
        layer.visible ? renderLayer(layer, props) : null
      )}

      {/* Headline + Subheadline above all layers */}
      {(templateConfig.headline.visible ?? true) && (
        <HeadlineLayer config={templateConfig.headline} text={activeHeadline} />
      )}
      {(templateConfig.subheadline.visible ?? true) && (
        <SubheadlineLayer config={templateConfig.subheadline} text={activeSubheadline} />
      )}
    </AbsoluteFill>
  );
};

// ─── Layer dispatcher ─────────────────────────────────────────

function renderLayer(layer: TemplateLayer, props: CompositionProps): React.ReactNode {
  const { assets, breakingNewsText, tickerText, reporterName, location, date } = props;

  switch (layer.type) {
    case 'background':
      return (
        <BackgroundLayer key={layer.id} layer={layer as BackgroundLayerType}
          asset={assets[(layer as BackgroundLayerType).assetKey]} />
      );
    case 'anchor':
      return <AnchorLayer key={layer.id} layer={layer as AnchorLayerType} asset={assets.anchorVideo} />;
    case 'logo':
      return <LogoLayer key={layer.id} layer={layer as LogoLayerType} asset={assets.logo} />;
    case 'top-bar':
      return <TopBarLayer key={layer.id} layer={layer as TopBarLayerType} />;
    case 'lower-third':
      return <LowerThirdLayer key={layer.id} layer={layer as LowerThirdLayerType}
        reporterName={reporterName} location={location} date={date} />;
    case 'ticker':
      return <TickerLayer key={layer.id} layer={layer as TickerLayerType} tickerText={tickerText} />;
    case 'breaking-news-banner':
      return <BreakingNewsBannerLayer key={layer.id} layer={layer as BannerLayerType}
        breakingNewsText={breakingNewsText} />;
    case 'watermark':
      return <WatermarkLayer key={layer.id} layer={layer as WatermarkLayerType} asset={assets.watermark} />;
    case 'audio':
      return <AudioLayer key={layer.id} layer={layer as AudioLayerType}
        asset={assets[(layer as AudioLayerType).assetKey]} />;
    case 'image':
      return <ImageLayer key={layer.id} layer={layer as ImageLayerType}
        asset={assets[(layer as ImageLayerType).assetKey]} />;
    case 'video':
      return <VideoLayer key={layer.id} layer={layer as VideoLayerType}
        asset={assets[(layer as VideoLayerType).assetKey]} />;
    default:
      return null;
  }
}
